import { useCallback, useEffect, useMemo, useRef } from 'react';

import _ from 'lodash';

import { FuelType } from 'pages/QuoteView/Quoting/RequestQuickQuote/QuickQuoteForm';
import { SelectedQuoteType } from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { Maybe, MaybeUndef } from 'types/UtilityTypes';

export interface UseFuelSurchargeAutoSelectParams {
  parentQuoteRequestId: number;
  selectedQuoteType?: SelectedQuoteType;
  defaultFSCProvider?: string;
  datFuelSurcharge: Maybe<number>;
  portalFuelSurcharge: Maybe<number>;
  customerTableFuelSurcharge?: MaybeUndef<number>;
  kencoFuelSurcharge?: Maybe<number>;
  fuelPriceEIA: Maybe<number>;
  applyFuelTypeSelection: (type: FuelType) => void;
  // Clears selectedFuelType so toggles/tooltip stop attributing a provider rate.
  clearFuelTypeAttribution: () => void;
}

export interface UseFuelSurchargeAutoSelectResult {
  selectUserFuelType: (type: FuelType) => void;
  markManualFuelEntry: () => void;
}

// Auto-selects an FSC provider when rates arrive, unless the user has toggled a
// provider or typed a manual value. User intent is kept via refs until
// parentQuoteRequestId changes (same idea as hasUserSelectedCard for quote cards).
export function useFuelSurchargeAutoSelect({
  parentQuoteRequestId,
  selectedQuoteType,
  defaultFSCProvider,
  datFuelSurcharge,
  portalFuelSurcharge,
  customerTableFuelSurcharge,
  kencoFuelSurcharge,
  fuelPriceEIA,
  applyFuelTypeSelection,
  clearFuelTypeAttribution,
}: UseFuelSurchargeAutoSelectParams): UseFuelSurchargeAutoSelectResult {
  const userFuelTypeRef = useRef<Maybe<FuelType>>(null);
  const hasManualFuelEntryRef = useRef(false);
  const trackedQuoteRequestIdRef = useRef(parentQuoteRequestId);
  const isOnKencoFlash =
    selectedQuoteType === SelectedQuoteType.KENCO_FLASH_AVERAGE;

  if (trackedQuoteRequestIdRef.current !== parentQuoteRequestId) {
    trackedQuoteRequestIdRef.current = parentQuoteRequestId;
    userFuelTypeRef.current = null;
    hasManualFuelEntryRef.current = false;
  }

  const fuelRatesByType = useMemo(
    (): Partial<Record<FuelType, MaybeUndef<number>>> => ({
      [FuelType.DAT]: datFuelSurcharge,
      [FuelType.Portal]: portalFuelSurcharge,
      [FuelType.CustomerTable]: customerTableFuelSurcharge,
      [FuelType.Kenco]: kencoFuelSurcharge,
      [FuelType.DOE]:
        fuelPriceEIA != null ? _.round(fuelPriceEIA * 0.143, 2) : null,
    }),
    [
      datFuelSurcharge,
      portalFuelSurcharge,
      customerTableFuelSurcharge,
      kencoFuelSurcharge,
      fuelPriceEIA,
    ]
  );

  useEffect(() => {
    // Determines the fuel type to use based on the provider hierarchy and available FSC data
    const fuelTypeMap: { [key: string]: FuelType } = {
      DAT: FuelType.DAT,
      DOE: FuelType.DOE,
      Portal: FuelType.Portal,
      KENCO: FuelType.Kenco,
      CUSTOMER_TABLE: FuelType.CustomerTable,
    };

    // Force Kenco Flash FSC rate when the Kenco average card is selected.
    // Clear prior user lock so leaving Flash re-runs auto-select fresh.
    if (isOnKencoFlash && kencoFuelSurcharge) {
      userFuelTypeRef.current = null;
      hasManualFuelEntryRef.current = false;
      applyFuelTypeSelection(FuelType.Kenco);
      return;
    }

    const activeUserFuelType = userFuelTypeRef.current;
    if (activeUserFuelType != null) {
      if (fuelRatesByType[activeUserFuelType] != null) {
        applyFuelTypeSelection(activeUserFuelType);
        return;
      }
      userFuelTypeRef.current = null;
    }

    if (hasManualFuelEntryRef.current) {
      return;
    }

    if (customerTableFuelSurcharge != null) {
      applyFuelTypeSelection(FuelType.CustomerTable);
      return;
    }

    const configFuelType =
      fuelTypeMap[defaultFSCProvider?.toUpperCase() || ''] ?? null;
    const effectiveConfigFuelType: Maybe<FuelType> =
      configFuelType === FuelType.CustomerTable ? null : configFuelType;
    // Kenco bases rates on their own FSC Table, so we hide DOE rates to prevent confusion
    const shouldHideDOE = Boolean(kencoFuelSurcharge);
    // We're safe to always apply the config fuel type, unless it's set for DOE and DOE is hidden
    const canApplyConfigFuelType =
      !shouldHideDOE || effectiveConfigFuelType !== FuelType.DOE;

    const autoSelectedFuelType: Maybe<FuelType> =
      (canApplyConfigFuelType ? effectiveConfigFuelType : null) ||
      (portalFuelSurcharge ? FuelType.Portal : null) ||
      (datFuelSurcharge ? FuelType.DAT : null) ||
      (kencoFuelSurcharge ? FuelType.Kenco : null) ||
      (fuelPriceEIA && !shouldHideDOE ? FuelType.DOE : null);

    if (autoSelectedFuelType) {
      applyFuelTypeSelection(autoSelectedFuelType);
    }
  }, [
    applyFuelTypeSelection,
    customerTableFuelSurcharge,
    datFuelSurcharge,
    defaultFSCProvider,
    fuelPriceEIA,
    fuelRatesByType,
    isOnKencoFlash,
    kencoFuelSurcharge,
    parentQuoteRequestId,
    portalFuelSurcharge,
  ]);

  const selectUserFuelType = useCallback(
    (type: FuelType) => {
      // When Kenco Flash has a usable Kenco rate, other toggles are hidden. ignore
      // stray non-Kenco clicks. If Kenco FSC is missing/zero, other toggles remain
      // visible and must still work.
      if (isOnKencoFlash && kencoFuelSurcharge && type !== FuelType.Kenco) {
        return;
      }
      hasManualFuelEntryRef.current = false;
      userFuelTypeRef.current = type;
      applyFuelTypeSelection(type);
    },
    [applyFuelTypeSelection, isOnKencoFlash, kencoFuelSurcharge]
  );

  const markManualFuelEntry = useCallback(() => {
    userFuelTypeRef.current = null;
    hasManualFuelEntryRef.current = true;
    clearFuelTypeAttribution();
  }, [clearFuelTypeAttribution]);

  const result: UseFuelSurchargeAutoSelectResult = {
    selectUserFuelType,
    markManualFuelEntry,
  };

  return result;
}
